import { useQuery } from '@apollo/client';
import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { DetailsList } from '@eluve/blocks';
import { Grid, Heading, LinkButton, Separator, Stack } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import {
  formatHumanName,
  getDatadogUrlForUser,
  getPosthogUrlForUser,
} from '@eluve/utils';

import { UpdateUserAction } from './UpdateUserAction';

const GetUserInfoQuery = graphql(`
  query gerUserInfo($id: uuid!) {
    usersByPk(id: $id) {
      id
      firstName
      lastName
      email
      createdAt
      appointmentsAggregate {
        aggregate {
          count
        }
      }
      tenants {
        userId
        role
        tenantId
        tenant {
          name
        }
      }
    }
  }
`);

export const UserPage: React.FC<{ postHogProjectId: string }> = ({
  postHogProjectId,
}) => {
  const { userId } = useParams<{ userId: string }>();

  const { loading, data, error } = useQuery(GetUserInfoQuery, {
    variables: {
      id: userId ?? 'no-id',
    },
  });

  let datadogUrl;
  let posthogUrl;
  if (userId) {
    datadogUrl = getDatadogUrlForUser(userId);
    posthogUrl = getPosthogUrlForUser(postHogProjectId, userId);
  }

  return (
    <div>
      <div className="my-2 w-full py-2">
        <Heading>User Details</Heading>
        <Stack justify="between" wrap gap={2} direction="horizontal">
          <Heading
            size="2xl"
            weight="semibold"
          >{`ID: ${userId ?? '-'}`}</Heading>
          {data && userId && (
            <Stack inline fullWidth={false} gap={2} direction="horizontal">
              {datadogUrl && (
                <LinkButton variant="outline" href={datadogUrl}>
                  View Datadog Logs
                </LinkButton>
              )}
              {posthogUrl && (
                <LinkButton variant="outline" href={posthogUrl}>
                  View Posthog Events
                </LinkButton>
              )}
              <UpdateUserAction
                userId={userId}
                defaultValues={{
                  firstName: data.usersByPk?.firstName ?? '',
                  lastName: data.usersByPk?.lastName ?? '',
                  email: data.usersByPk?.email ?? '',
                }}
              />
            </Stack>
          )}
        </Stack>
      </div>

      <Stack className="ml-14" gap={2} direction="horizontal">
        {loading && (
          <Stack direction="horizontal" fullWidth={false} gap={2}>
            Loading ...
          </Stack>
        )}
        {error && (
          <Heading size="xl" weight="semibold">
            <span className="text-text-negative">Unable to find User</span>
          </Heading>
        )}
      </Stack>

      {data && (
        <Stack align="stretch" gap={2}>
          <DetailsList
            title={{ label: 'Info' }}
            items={[
              {
                label: 'Name',
                value: formatHumanName(
                  data.usersByPk?.firstName,
                  data.usersByPk?.lastName,
                ),
              },
              { label: 'Email', value: data.usersByPk?.email },
              {
                label: 'Associated Appointments',
                value:
                  data.usersByPk?.appointmentsAggregate?.aggregate?.count ??
                  '-',
              },
            ]}
          />
          <Stack className="rounded-lg p-6 shadow-md" gap={2}>
            <Heading size="xl" weight="semibold">
              Associated Organizations
            </Heading>
            <Separator className="my-2" />
            <Grid columns={2} className="gap-x-8 gap-y-3">
              <b>Name</b>
              <b>Role</b>
              {data.usersByPk?.tenants.map(({ role, tenantId, tenant }) => (
                <React.Fragment key={tenantId}>
                  <Link
                    to={`/admin/organizations/${tenantId}`}
                    className="hover:underline"
                  >
                    {tenant.name}
                  </Link>
                  <p>{role}</p>
                </React.Fragment>
              ))}
            </Grid>
          </Stack>
        </Stack>
      )}
    </div>
  );
};
